import { LRUCache } from 'lru-cache';
import { z } from 'zod';

// Types
export interface DataSourceConfig {
  type: 'api' | 'database' | 'file' | 'stream';
  endpoint?: string;
  authentication?: 'none' | 'apiKey' | 'oauth' | 'custom';
  rateLimit?: { requests: number; period: string };
  schema?: z.ZodSchema;
  requiredPermissions?: string[];
  dataClassification?: 'public' | 'internal' | 'confidential' | 'restricted';
  retryPolicy?: RetryPolicy;
  timeout?: number;
  fetcher: (params: any) => Promise<any>;
  cacheConfig?: CacheConfig;
}

export interface RetryPolicy {
  maxAttempts: number;
  backoffMultiplier: number;
  maxBackoffSeconds: number;
  retryableErrors?: string[];
}

export interface CacheConfig {
  ttl?: number; // Time to live in milliseconds
  maxSize?: number;
  staleWhileRevalidate?: boolean;
}

interface CachedData {
  data: any;
  timestamp: number;
  etag?: string;
}

export class DataSourceRegistry {
  private sources = new Map<string, DataSourceConfig>();
  private caches = new Map<string, LRUCache<string, CachedData>>();

  register(name: string, config: DataSourceConfig): void {
    // Validate the configuration
    this.validateConfig(name, config);

    // Store the source configuration
    this.sources.set(name, config);

    // Setup cache if configured
    if (config.cacheConfig) {
      const cache = new LRUCache<string, CachedData>({
        max: config.cacheConfig.maxSize || 100,
        ttl: config.cacheConfig.ttl || 1000 * 60 * 5 // 5 minutes default
      });
      this.caches.set(name, cache);
    }
  }

  async fetch(source: string, params: any = {}): Promise<any> {
    const config = this.sources.get(source);

    if (!config) {
      throw new Error(`Unknown data source: ${source}`);
    }

    // Check cache first if enabled
    const cache = this.caches.get(source);
    if (cache && config.cacheConfig) {
      const cacheKey = this.generateCacheKey(source, params);
      const cached = cache.get(cacheKey);

      if (cached) {
        // Check if data is still fresh
        const age = Date.now() - cached.timestamp;
        const ttl = config.cacheConfig.ttl || 1000 * 60 * 5;

        if (age < ttl) {
          return { ...cached.data, _cached: true };
        }

        // Handle stale-while-revalidate
        if (config.cacheConfig.staleWhileRevalidate) {
          // Return stale data and refresh in background
          this.refreshInBackground(source, params, cacheKey);
          return { ...cached.data, _cached: true, _stale: true };
        }
      }
    }

    try {
      // Execute the fetcher with retry logic
      const result = await this.fetchWithRetry(config, params);

      // Cache the result if cache is enabled
      if (cache) {
        const cacheKey = this.generateCacheKey(source, params);
        cache.set(cacheKey, {
          data: result,
          timestamp: Date.now()
        });
      }

      return result;
    } catch (error) {
      // If we have cached data, return it as fallback
      if (cache) {
        const cacheKey = this.generateCacheKey(source, params);
        const cached = cache.get(cacheKey);
        if (cached) {
          return { ...cached.data, _cached: true, _fallback: true };
        }
      }

      // Log error and re-throw
      console.error(`Data fetch failed for source ${source}:`, error);
      throw error;
    }
  }

  private async fetchWithRetry(config: DataSourceConfig, params: any): Promise<any> {
    const retryPolicy = config.retryPolicy || {
      maxAttempts: 3,
      backoffMultiplier: 2,
      maxBackoffSeconds: 30
    };

    let lastError: Error | null = null;
    let delay = 1000; // Start with 1 second

    for (let attempt = 0; attempt < retryPolicy.maxAttempts; attempt++) {
      try {
        // Validate input if schema is provided
        if (config.schema) {
          const validation = config.schema.safeParse(params);
          if (!validation.success) {
            throw new Error(`Invalid parameters: ${validation.error.message}`);
          }
        }

        // Apply rate limiting if configured
        if (config.rateLimit) {
          await this.checkRateLimit(config);
        }

        // Execute the fetcher with timeout
        const timeoutPromise = new Promise((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), config.timeout || 30000);
        });

        const fetchPromise = config.fetcher(params);
        
        return await Promise.race([fetchPromise, timeoutPromise]);
      } catch (error) {
        lastError = error as Error;

        // Check if error is retryable
        if (retryPolicy.retryableErrors) {
          const errorMessage = error instanceof Error ? error.message : String(error);
          const isRetryable = retryPolicy.retryableErrors.some(pattern => 
            errorMessage.includes(pattern)
          );
          if (!isRetryable) throw error;
        }

        // Don't retry on last attempt
        if (attempt === retryPolicy.maxAttempts - 1) throw error;

        // Calculate backoff delay
        await this.sleep(Math.min(delay, retryPolicy.maxBackoffSeconds * 1000));
        delay *= retryPolicy.backoffMultiplier;
      }
    }

    throw lastError || new Error('Max retry attempts reached');
  }

  getStats(source: string) {
    const cache = this.caches.get(source);

    if (!this.sources.has(source)) return null;

    return {
      cache: cache ? {
        size: cache.size,
        calculatedSize: cache.calculatedSize
      } : null
    };
  }

  getAllSources(): string[] {
    return Array.from(this.sources.keys());
  }

  getSourceConfig(source: string): DataSourceConfig | undefined {
    return this.sources.get(source);
  }

  clearCache(source?: string): void {
    if (source) {
      const cache = this.caches.get(source);
      if (cache) cache.clear();
    } else {
      // Clear all caches
      this.caches.forEach(cache => cache.clear());
    }
  }

  private validateConfig(name: string, config: DataSourceConfig): void {
    if (!name || name.trim().length === 0) {
      throw new Error('Data source name cannot be empty');
    }

    if (!config.fetcher || typeof config.fetcher !== 'function') {
      throw new Error('Data source must have a valid fetcher function');
    }

    if (config.type === 'api' && !config.endpoint) {
      throw new Error('API data sources must specify an endpoint');
    }
  }

  private generateCacheKey(source: string, params: any): string {
    return `${source}:${JSON.stringify(params)}`;
  }

  private async refreshInBackground(source: string, params: any, cacheKey: string): Promise<void> {
    const config = this.sources.get(source);
    const cache = this.caches.get(source);

    if (!config || !cache) return;

    try {
      const result = await this.fetchWithRetry(config, params);
      cache.set(cacheKey, {
        data: result,
        timestamp: Date.now()
      });
    } catch (error) {
      // Silent fail for background refresh
      console.error(`Background refresh failed for ${source}:`, error);
    }
  }

  private async checkRateLimit(_config: DataSourceConfig): Promise<void> {
    // Simple rate limiting implementation
    // In production, use a more sophisticated rate limiter
    // This is a placeholder that doesn't actually limit
    await Promise.resolve();
  }

  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}

// Export singleton instance
export const dataSourceRegistry = new DataSourceRegistry();

// Helper function to create a data source from existing widget fetchers
export function createDataSourceFromWidget(
  name: string,
  fetcher: (params: any) => Promise<any>,
  options: Partial<DataSourceConfig> = {}
): void {
  dataSourceRegistry.register(name, {
    type: 'api',
    fetcher,
    cacheConfig: {
      ttl: 1000 * 60 * 5, // 5 minutes
      maxSize: 100,
      staleWhileRevalidate: true
    },
    retryPolicy: {
      maxAttempts: 3,
      backoffMultiplier: 2,
      maxBackoffSeconds: 10,
      retryableErrors: ['ECONNRESET', 'ETIMEDOUT', 'ENOTFOUND']
    },
    timeout: 30000,
    ...options
  });
}